// STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 리모컨(가상 펜) 신호 판별 · 상품 목록 조작 결정 (순수 함수).
// 좌표·방향은 하드코딩하지 않는다. 판매자 기기에서 버튼별로 '학습'한 신호와만 비교한다.
export const BUTTONS = ['center', 'up', 'down', 'left', 'right'];
export const BTN_LABEL = { center: '중앙', up: '위', down: '아래', left: '왼쪽', right: '오른쪽' };
export const TAP_PX = 12;
export const TOL = 0.035;
export const CLOSE_TOP = '__remote_close_top__';
export const CLOSE_BOTTOM = '__remote_close_bottom__';
// 선택 인덱스 → 목록 행 id (-1 = 위쪽 닫기, count = 아래쪽 닫기).
// QUICK-MENU-SLIDE-1 · 목록 끝 '메뉴' 가상 항목 제거 → 목록 열림 중 '왼쪽' 1회로 통합 메뉴 진입.
export const cursorId = (items, index) => (index < 0 ? CLOSE_TOP : index >= items.length ? CLOSE_BOTTOM : items[index]?.product_id || null);

// 화면 전체 기준 좌표(screenX/Y) → 비율 · 탭/밀기 · 실제 이동 축(+x/-x/+y/-y).
export const sigFromGesture = ({ sx, sy, ex, ey, sw, sh, cancelled = false }) => {
  const dx = ex - sx;
  const dy = ey - sy;
  const kind = Math.hypot(dx, dy) < TAP_PX ? 'tap' : 'swipe';
  const dir = kind === 'tap' ? null : Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? '+x' : '-x') : (dy > 0 ? '+y' : '-y');
  return { nx: Math.round((sx / sw) * 1000) / 1000, ny: Math.round((sy / sh) * 1000) / 1000, kind, dir, cancelled, sw, sh };
};

const near = (a, b, tol) => a.kind === b.kind && a.dir === b.dir && Math.abs(a.nx - b.nx) <= tol && Math.abs(a.ny - b.ny) <= tol;

// 정확히 1개 버튼과만 일치할 때 인식. 0개(미확인)·2개 이상(모호)은 무시.
// QUICK-REGISTER-1 · 하단 셔터(별도 학습 신호)와 비교용.
export const sameSignal = (a, b) => !!a && !!b && a.sw === b.sw && a.sh === b.sh && near(a, b, TOL);
// 셔터 학습값이 기존 5개 버튼과 겹치면 사용 불가.
export const shutterConflict = (map, sig) => BUTTONS.find((b) => map && map[b] && map[b].sw === sig.sw && near(map[b], sig, TOL * 2)) || null;
export const classify = (map, sig) => {
  if (!map || !sig) return { button: null, reason: 'unknown' };
  const hits = BUTTONS.filter((b) => map[b] && map[b].sw === sig.sw && map[b].sh === sig.sh && near(map[b], sig, TOL));
  if (hits.length === 1) return { button: hits[0], reason: 'ok' };
  return { button: null, reason: hits.length ? 'ambiguous' : 'unknown' };
};

// 학습 결과 검증: 5개 모두 학습 · 서로 겹치지 않음(허용 오차 2배) · 같은 화면 크기.
export const validateMap = (map) => {
  const missing = BUTTONS.filter((b) => !map || !map[b]);
  if (missing.length) return { ok: false, reason: `미학습: ${missing.map((b) => BTN_LABEL[b]).join(', ')}` };
  for (let i = 0; i < BUTTONS.length; i += 1) {
    for (let j = i + 1; j < BUTTONS.length; j += 1) {
      const a = map[BUTTONS[i]]; const b = map[BUTTONS[j]];
      if (near(a, b, TOL * 2)) return { ok: false, reason: `${BTN_LABEL[BUTTONS[i]]}·${BTN_LABEL[BUTTONS[j]]} 신호가 너무 비슷해 구분 불가` };
    }
  }
  const sizes = new Set(BUTTONS.map((b) => `${map[b].sw}x${map[b].sh}`));
  if (sizes.size !== 1) return { ok: false, reason: '학습 중 화면 크기가 바뀜 · 다시 학습' };
  return { ok: true, reason: 'ok' };
};

// 상품 목록 조작 결정. 목록 이동(cursor)은 방송 소개 상품을 바꾸지 않는다.
// state: { cursor: number|null, moved: boolean } · ctx: { open, count, activeIdx }
export const decide = (state, button, { open, count, activeIdx, menu = false }) => {
  const start = activeIdx >= 0 ? activeIdx : 0;
  if (!open) {
    if (button === 'center') return { state: { cursor: start, moved: false }, action: { type: 'open' } };
    if (button === 'up' || button === 'left') return { state, action: { type: 'relative', delta: -1 } };
    if (button === 'down' || button === 'right') return { state, action: { type: 'relative', delta: 1 } };
    return { state, action: null };
  }
  const cur = state.cursor == null ? start : state.cursor;
  // LIST-CLOSE-1 · 목록 앞(-1)·뒤(count) 에 '목록 닫기' 가상 항목 (상품 수에 미포함).
  if (button === 'up' || button === 'down') {
    if (!count) return { state, action: null };
    const next = Math.max(-1, Math.min(count, cur + (button === 'up' ? -1 : 1)));
    return { state: { cursor: next, moved: state.moved || next !== cur }, action: { type: 'cursor', index: next } };
  }
  if (button === 'center') {
    const reset = { cursor: null, moved: false };
    if (cur < 0 || cur >= count) return { state: reset, action: { type: 'close', cancel: true } };
    if (state.moved && count) return { state: reset, action: { type: 'pick', index: cur } };
    return { state: reset, action: { type: 'close' } };
  }
  // QUICK-MENU-SLIDE-1 · 메뉴 사용 시 목록 열림 + '왼쪽' = 통합 메뉴 (소개 상품·선택 위치 변경 없음).
  if (button === 'left' && menu) return { state: { cursor: cur, moved: state.moved }, action: { type: 'menu' } };
  if (button === 'left' || button === 'right') {
    return { state: { cursor: null, moved: false }, action: { type: 'close_relative', delta: button === 'left' ? -1 : 1 } };
  }
  return { state, action: null };
};

// STUDIO-BR-TX3-TIMER-NOTICE-CONTROL-1 · 공지·타이머 메뉴 결정 (상품 조작과 분리된 모드).
// state: { level: 'root'|'timer'|'notice'|'ext', idx } · len: 현재 하위 목록 길이
// MENU-EXIT-QUICKADD-COLLAPSE-1 · 맨 위 '나가기'(방송 화면 복귀) · '빠른등록'(기존 QuickAddModal).
export const MENU_ROOT = ['leave', 'product', 'quick', 'past', 'timer', 'notice', 'ext'];
export const MENU_START = MENU_ROOT.indexOf('past');
export const decideMenu = (st, button, { len = 0 } = {}) => {
  const n = st.level === 'root' ? MENU_ROOT.length : len;
  if (button === 'up' || button === 'down') {
    if (!n) return { state: st, action: null };
    const idx = Math.max(0, Math.min(n - 1, st.idx + (button === 'up' ? -1 : 1)));
    return { state: { ...st, idx }, action: { type: 'move', idx } };
  }
  if (button === 'left') {
    if (st.level === 'root') return { state: st, action: { type: 'exit' } };
    return { state: { level: 'root', idx: MENU_ROOT.indexOf(st.level) }, action: { type: 'back' } };
  }
  if (st.level === 'root' && (button === 'center' || button === 'right')) {
    const key = MENU_ROOT[st.idx];
    if (key === 'product') return { state: st, action: { type: 'exit' } };
    if (key === 'leave') return { state: st, action: { type: 'leave' } };
    if (key === 'quick') return { state: st, action: { type: 'quick' } };
    return { state: { level: key, idx: 0 }, action: { type: 'enter', level: key } };
  }
  if (button === 'center' && n) return { state: st, action: { type: 'run', level: st.level, idx: Math.min(st.idx, n - 1) } };
  return { state: st, action: null };
};
